import { Box, Stack, useMediaQuery } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';

import { PRIORITY_COLORS } from '@/constants/chartcolors.constants';

import useStyles from './PercentageBarWidget.styles';
import type {
  PercentageBarDataType,
  PercentageBarProps
} from './PercentageBarWidget.types';

/*
  Percentage Bar Widget
  This component is used to display a list of items with a progress bar.
  Data values can either be numbers or strings.
  If the data type is count, total will be calculated as sum of all data values and data value and percentage will be displayed on above bar
  If the data value is percent, total will not be calculated and data value as percentage will be displayed on above bar
*/
const PercentageBarWidget = (props: PercentageBarProps) => {
  const { data, handleBarClick } = props;
  const { classes } = useStyles({
    length: data?.length,
    hasClick: !!handleBarClick
  });
  const minBreakpointHeight = useMediaQuery('(min-height:1700px)');

  let total = 0;
  data?.forEach((item: PercentageBarDataType) => {
    if (item.type === 'percent') return;
    total += item.data[0] as number;
  });

  const getBarValue = (value: PercentageBarDataType) => {
    if (value.type === 'count' && total !== 0) {
      return Math.round((value.data[0] / total) * 100);
    }
    if (value.type === 'percent') {
      return value.data[0];
    }
    return 0;
  };

  const hasData =
    !!data &&
    data.length > 0 &&
    data.map((item) => getBarValue(item)).reduce((a, b) => a + b, 0) !== 0;

  return (
    <div className={classes.percentageBarCardContent}>
      <Stack
        spacing={minBreakpointHeight ? 1.5 : 1}
        className={classes.contentContainer}
      >
        {!hasData ? (
          <Box className={classes.noDataBox}>
            <Typography color="text.secondary">No data</Typography>
          </Box>
        ) : (
          data?.map((item: PercentageBarDataType, index: number) => (
            <div
              key={item.name}
              style={{
                marginBottom: index === 4 ? 0 : '',
                marginTop: index === 0 ? 0 : ''
              }}
              className={classes.barContainer}
            >
              <Box>
                <Typography className={classes.labelText} variant="h5">
                  {item.name}
                  {item.type === 'count' && (
                    <span className={classes.ticketNumber}>
                      &#40;{item.data[0]}&#41;
                    </span>
                  )}
                  <span className={classes.ticketPercent}>
                    {getBarValue(item)}%
                  </span>
                </Typography>
              </Box>
              <LinearProgress
                onClick={() =>
                  handleBarClick
                    ? handleBarClick(item.name, item.data[0].toString())
                    : null
                }
                sx={{
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: item.color
                      ? item.color
                      : PRIORITY_COLORS[
                          item.name as keyof typeof PRIORITY_COLORS
                        ]
                  }
                }}
                className={classes.progressBar}
                variant="determinate"
                value={getBarValue(item)}
              />
            </div>
          ))
        )}
      </Stack>
    </div>
  );
};

PercentageBarWidget.displayName = 'PercentageBarWidget';

export default PercentageBarWidget;
